import React, { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Container,
  createStyles,
  Grid,
  makeStyles,
  TextField,
  Theme,
  Typography,
  Button as MuiButton,
} from '@material-ui/core';
import IconInfo from '~/images/icon-info.svg';
import IconBackArrow from '~/images/impacttool/icon-arrow-back.svg';
import IconCheck from '~/images/impacttool/icon-check.svg';
import {
  blueColor,
  darkGreenColor,
  emptyCriterion,
  greenColor,
  redColor,
  ResultValueUnit,
} from '.';
import { Tooltip } from '../Tooltip';
import { Criterion } from '~/types/impactTool';
import { greyColor } from './constants';
import { writeCriterions } from '~/utils/storageHandlerToolForm';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      [theme.breakpoints.up('sm')]: {
        minHeight: 270,
      },
    },
    shortContainer: {
      minHeight: 160,
    },
    noFixedHeight: {
      minHeight: 'none',
    },
  })
);

export const ResultBox: React.FC<{
  keyword: string;
  headline?: string;
  value?: string | number;
  unit: ResultValueUnit;
  tooltip?: string;
  iconComponent: ReactElement;
  twoColumn?: boolean;
  short?: boolean;
  noFixedHeight?: boolean;
}> = ({
  keyword,
  headline,
  value,
  unit,
  tooltip,
  iconComponent,
  twoColumn,
  short = false,
  noFixedHeight = false,
  children,
}) => {
  const classes = useStyles();

  return (
    <Container
      style={{
        border: '2px solid ' + blueColor,
        borderRadius: '5px',
        margin: short ? '0.75rem 0 0.5rem 0' : '2rem 0 1rem 0',
        padding: short ? '0.5rem ' : '1rem',
        position: 'relative',
      }}
      className={
        noFixedHeight
          ? classes.noFixedHeight
          : !short
          ? classes.container
          : classes.shortContainer
      }
    >
      <Box
        style={{
          border: '2px solid ' + blueColor,
          borderRadius: '100%',
          width: short ? 37 : 55,
          height: short ? 37 : 55,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'absolute',
          backgroundColor: 'white',
          top: short ? -18 : -30,
          left: 0,
          right: 0,
          margin: 'auto',
        }}
      >
        {iconComponent}
      </Box>
      <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          variant="body2"
          style={{
            color: blueColor,
            fontWeight: 'bold',
            fontSize: short ? 12 : '1rem',
          }}
        >
          {keyword}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip}>
            <IconInfo />
          </Tooltip>
        )}
      </Box>
      {!twoColumn && (
        <ResultContent
          value={value}
          unit={unit}
          headline={headline}
          short={short}
        >
          {children}
        </ResultContent>
      )}
      {twoColumn && children}
    </Container>
  );
};

export const ResultContent: React.FC<{
  value?: string | number;
  unit?: ResultValueUnit;
  headline?: string;
  tooltip?: string;
  short?: boolean;
}> = ({
  value = 0,
  unit = ResultValueUnit.EURO,
  headline = '',
  tooltip,
  short = false,
  children,
}) => {
  return (
    <Box>
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '0.5rem',
        }}
      >
        <Typography
          variant="h3"
          style={{
            hyphens: 'auto',
            marginBottom: short ? '0.25rem' : '0.75rem',
            fontSize: short ? '1rem' : '1.4583rem',
          }}
        >
          {headline}
        </Typography>
        <ColouredCostBig value={value} unit={unit} />
      </Box>
      <Typography
        variant="body2"
        style={{ fontSize: short ? 12 : 14, lineHeight: short ? 1.2 : 1.43 }}
      >
        {children}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} right>
          <IconInfo />
        </Tooltip>
      )}
    </Box>
  );
};

export const ColouredCostBig: React.FC<{
  value: string | number;
  unit: ResultValueUnit;
  component?: React.ElementType;
  small?: boolean;
}> = ({ value, unit, component = 'h3', small = false }) => {
  value = convertStringToNumber(value);
  return (
    <Typography
      variant="h3"
      component={component}
      style={{
        color: value >= 0 ? (value === 0 ? greyColor : redColor) : greenColor,
        whiteSpace: 'nowrap',
        fontSize: small ? 16.8 : '1.4583rem',
      }}
    >
      {value > 0 ? '+' : ''}
      {!isNaN(value) &&
        (unit === ResultValueUnit.EURO || unit === ResultValueUnit.HOURS) &&
        Math.round(value).toLocaleString('de') + ' ' + unit.toString()}
      {!isNaN(value) &&
        unit !== ResultValueUnit.EURO &&
        unit !== ResultValueUnit.HOURS &&
        (Math.round(value * 10) / 10).toLocaleString('de') +
          ' ' +
          unit.toString()}
      {isNaN(value) && 0 + ' ' + unit.toString()}
    </Typography>
  );
};

export const ColouredCost: React.FC<{
  value: string | number;
  unit: ResultValueUnit;
  onlyGreen?: boolean;
  color?: string;
  signVisible?: boolean;
}> = ({ value, unit, onlyGreen = false, color, signVisible }) => {
  value = convertStringToNumber(value);
  let setColor;

  if (color) {
    setColor = color;
  } else if (value > 0) {
    if (onlyGreen) {
      setColor == greenColor;
    } else {
      setColor = redColor;
    }
  } else if (value == 0) {
    setColor = greyColor;
  } else {
    setColor = darkGreenColor;
  }

  return (
    <Typography
      variant="body2"
      style={{
        color: setColor,
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
      }}
      component="span"
    >
      {signVisible && value > 0 ? '+' : ''}
      {signVisible && value < 0 ? '-' : ''}
      {!isNaN(value) && Math.round(Math.abs(value)).toLocaleString('de')}{' '}
      {isNaN(value) && 0} {unit.toString()}
    </Typography>
  );
};

export const BackLink: React.FC<{
  href: string;
  label: string;
}> = ({ href, label }) => {
  return (
    <a
      href={href}
      style={{
        fontSize: 16,
        display: 'flex',
        alignItems: 'center',
        margin: '1rem 0',
        color: '#576164',
        textDecorationColor: greenColor,
      }}
    >
      <IconBackArrow />
      {label}
    </a>
  );
};

export const CriterionList: React.FC<{
  criterions: Array<Criterion>;
  callback?;
  onChange?;
  setCriterions?: Array<Criterion>;
}> = ({ criterions, callback, onChange, setCriterions }) => {
  /** Hold all user input and default criterions */
  const [criterionList, setCriterionList] = useState(criterions);

  useEffect(() => {
    if (setCriterions) {
      setCriterionList(setCriterions);
    }
  }, [setCriterions]);

  /**  Set whole criterion list again with new fulfilled state*/
  const handleCriterionClick = (i: number) => {
    if (JSON.stringify(criterionList[i]) != JSON.stringify(emptyCriterion)) {
      setCriterionList((prevState) => {
        const newState = [...prevState];
        newState[i].fullfilled = !newState[i].fullfilled;
        return newState;
      });
      writeCriterions(criterionList);
      onChange(criterionList);
    }
  };

  // Send to parent if some criterions have been checked
  useEffect(
    function callbackCriterionListNotEmpty() {
      let someCriterionChecked = false;
      if (criterionList.some((c) => c.fullfilled == true)) {
        someCriterionChecked = true;
      } else if (criterionList.every((c) => c.fullfilled == false)) {
        someCriterionChecked = false;
      }
      callback(someCriterionChecked);
    },
    [criterionList]
  );

  const handleCriterionChange = (e, index) => {
    setCriterionList((prevState) => {
      const newState = [...prevState];
      newState[index].criterion = e.target.value;
      return newState;
    });
    writeCriterions(criterionList);
    onChange(criterionList);
  };

  const handleDescriptionChange = (e, index) => {
    setCriterionList((prevState) => {
      const newState = [...prevState];
      newState[index].description = e.target.value;
      return newState;
    });
    writeCriterions(criterionList);
    onChange(criterionList);
  };

  return (
    <Box mt={4}>
      <form>
        <Grid container>
          <Grid container style={{ fontWeight: 'bold', color: '#576164' }}>
            <Grid item xs={4}>
              Beurteilungs&shy;kriterium
            </Grid>
            <Grid item xs={2}>
              Trifft in unserer Kommune zu
            </Grid>
            <Grid item xs={5}>
              Beschreibung
            </Grid>
          </Grid>
          <Grid item xs={1}></Grid>
          {criterionList &&
            criterionList.map((criterion, index) => {
              return (
                <Grid container style={{ marginTop: 40 }} key={index}>
                  <Grid item xs={4}>
                    {!criterion.default && (
                      <TextField
                        style={{ fontSize: 16, fontStyle: 'italic' }}
                        multiline
                        minRows={3}
                        fullWidth
                        placeholder="Weitere Kriterien:
                        Formulieren Sie hier bei Bedarf weitere Kriterien."
                        defaultValue={criterion.criterion}
                        onBlur={(e) => handleCriterionChange(e, index)}
                        inputProps={{ maxLength: 400 }}
                      />
                    )}
                    {criterion.default && (
                      <Typography style={{ fontSize: 16, fontStyle: 'italic' }}>
                        &ldquo;{criterion.criterion}&rdquo;
                      </Typography>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        border: criterion.fullfilled
                          ? '2px solid #63a615'
                          : '2px solid #dadada',
                        boxShadow: '0 0 5px 0 rgba(0, 0, 0, 0.3)',
                        width: 56,
                        height: 56,
                        borderRadius: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        handleCriterionClick(index);
                      }}
                    >
                      {criterion.fullfilled && <IconCheck />}
                    </div>
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      multiline
                      minRows={3}
                      fullWidth
                      placeholder="Beschreibung für Ihren Standort"
                      defaultValue={criterion.description}
                      onBlur={(e) => handleDescriptionChange(e, index)}
                      inputProps={{ maxLength: 400 }}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    {criterion.tooltip && (
                      <Tooltip title={criterion.tooltip}>
                        <IconInfo />
                      </Tooltip>
                    )}
                  </Grid>
                </Grid>
              );
            })}
        </Grid>
      </form>
      <Box mt={3} mb={3}>
        <MuiButton
          type="button"
          onClick={() => {
            if (
              JSON.stringify(criterionList[criterionList.length - 1]) !=
              JSON.stringify(emptyCriterion)
            ) {
              setCriterionList([...criterionList, { ...emptyCriterion }]);
            }
          }}
          style={{
            padding: '8px 24px',
            borderRadius: 50,
            pointerEvents: 'auto',
            fontWeight: 'normal',
          }}
        >
          <span style={{ fontWeight: 'bold' }}>+&nbsp;</span>
          Weiteres Kriterium hinzufügen
        </MuiButton>
      </Box>
    </Box>
  );
};

export const SubjectiveInputs: React.FC<{
  defaultCriterions: Array<Criterion>;
  onChange?;
  setCriterions?: Array<Criterion>;
}> = ({ defaultCriterions, onChange, setCriterions }) => {
  const [criterionList] = useState([
    ...defaultCriterions,
    { ...emptyCriterion },
  ]);

  /** Status whether at least one criterion are set */
  const [someCriterions, setSomeCriterions] = useState(false);

  useEffect(() => {
    criterionList;
  }, [someCriterions]);

  return (
    <Box mt={4}>
      <Typography variant="h3" component="h3" style={{ marginBottom: '1rem' }}>
        Bitte beurteilen Sie, welche weiteren Wirkungen durch Ihre
        Fahrradabstellanlage erzielt werden
      </Typography>
      <Typography>
        Oft spielen beim Bau von Fahrradabstellanlagen an Bahnhöfen andere
        Aspekte als die zuvor quantitativ ermittelten eine wichtige Rolle. Wir
        schlagen Ihnen hier einige typische Wirkungen von Fahrradabstellanlagen
        vor. Bitte aktivieren Sie die Checkbox, wenn der jeweilige Aspekt auf
        Ihre Anlage zutrifft und beschreiben Sie in dem Feld rechts was konkret
        an Ihrem Standort für Wirkungen zu erwarten sind oder warum dieser
        Aspekt in Ihrer Kommune wichtig ist.
      </Typography>

      <CriterionList
        criterions={criterionList}
        callback={(someCriterionsChecked) =>
          setSomeCriterions(someCriterionsChecked)
        }
        onChange={(criterions) => onChange(criterions)}
        setCriterions={setCriterions}
      />
    </Box>
  );
};

const convertStringToNumber = (value: string | number): number => {
  if (typeof value === 'string') {
    return parseInt(value, 10);
  }
  return value;
};
